import React from "react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, ReferenceLine,
  PieChart, Pie,
} from "recharts";

export const PATTERN_COLORS = [
  "#F97316", "#FFB800", "#10B981", "#06B6D4", "#3B82F6", "#8B5CF6",
  "#EC4899", "#EF4444", "#84CC16", "#14B8A6", "#F59E0B", "#6366F1",
  "#D946EF", "#0EA5E9", "#22C55E", "#E11D48", "#A855F7", "#FB923C",
  "#2DD4BF", "#FACC15", "#4ADE80", "#38BDF8", "#F472B6", "#C084FC",
  "#FCA5A5", "#93C5FD", "#86EFAC",
];

const TIP = { contentStyle: { background: "#1A1410", border: "1px solid #FFB800", borderRadius: 10, color: "#FFF6E0", fontSize: 12 }, itemStyle: { color: "#FFF6E0" }, labelStyle: { display: "none" } };

const abbr = (s) => s.replace(/^Purva /, "P.").replace(/^Uttara /, "U.").slice(0, 6);
const shortLabel = (b, dim) => (dim === "house" ? `H${b.label}` : dim === "nakshatra" ? abbr(b.label) : b.label.slice(0, 3));
const colorOf = (panel, label) => PATTERN_COLORS[panel.buckets.findIndex((b) => b.label === label) % PATTERN_COLORS.length];

function Distribution({ panel }) {
  const data = panel.buckets.map((b) => ({ ...b, short: shortLabel(b, panel.dim) }));
  return (
    <ResponsiveContainer width="100%" height={240}>
      <BarChart data={data} margin={{ top: 10, right: 8, left: -18, bottom: 0 }}>
        <XAxis dataKey="short" tick={{ fontSize: 10, fill: "#94A3B8" }} interval={0} angle={panel.dim === "nakshatra" ? -50 : 0} textAnchor={panel.dim === "nakshatra" ? "end" : "middle"} height={panel.dim === "nakshatra" ? 50 : 24} />
        <YAxis tick={{ fontSize: 10, fill: "#94A3B8" }} unit="%" />
        <Tooltip {...TIP} cursor={{ fill: "rgba(255,184,0,0.08)" }} formatter={(v, _n, p) => [`${v}% · ${p.payload.count} charts`, p.payload.display]} />
        <ReferenceLine y={panel.expected_pct} stroke="#94A3B8" strokeDasharray="4 4" label={{ value: "chance", fontSize: 9, fill: "#94A3B8", position: "insideTopRight" }} />
        <Bar dataKey="pct" radius={[6, 6, 0, 0]} animationDuration={900}>
          {data.map((b, i) => <Cell key={b.label} fill={PATTERN_COLORS[i % PATTERN_COLORS.length]} />)}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

function Donut({ panel }) {
  const data = panel.ranking.map((b) => ({ name: b.display, value: b.count }));
  const rest = panel.multi ? panel.empty_house_count : panel.n - data.reduce((s, d) => s + d.value, 0);
  if (rest > 0) data.push({ name: panel.multi ? "House empty" : "Others", value: rest });
  return (
    <ResponsiveContainer width="100%" height={200}>
      <PieChart>
        <Pie data={data} dataKey="value" nameKey="name" innerRadius={48} outerRadius={80} paddingAngle={2} animationDuration={900}>
          {data.map((d, i) => <Cell key={d.name} fill={i >= panel.ranking.length ? "#E7DCC8" : colorOf(panel, panel.ranking[i].label)} />)}
        </Pie>
        <Tooltip {...TIP} formatter={(v, n) => [`${v} charts`, n]} />
      </PieChart>
    </ResponsiveContainer>
  );
}

function Ranking({ panel }) {
  return (
    <ol className="space-y-2" data-testid={`pattern-ranking-${panel.title}`}>
      {panel.ranking.map((b, i) => (
        <li key={b.label} className="flex items-center gap-3 text-sm">
          <span className="w-6 h-6 rounded-full grid place-items-center text-[11px] font-bold text-white" style={{ background: colorOf(panel, b.label) }}>{i + 1}</span>
          <span className="flex-1 text-[#3F2D1B]">{b.display}</span>
          <span className="text-[#3F2D1B]/70 tabular-nums">{b.count} · {b.pct}%</span>
          <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${b.lift >= 1.5 ? "bg-emerald-100 text-emerald-800" : b.lift >= 1 ? "bg-amber-100 text-amber-800" : "bg-stone-100 text-stone-600"}`}>{b.lift}×</span>
        </li>
      ))}
    </ol>
  );
}

export function PatternPanel({ panel, index }) {
  return (
    <article className="rounded-2xl bg-white border border-amber-200/70 p-5 sm:p-6 shadow-sm animate-in fade-in slide-in-from-bottom-2" style={{ animationDelay: `${index * 90}ms` }} data-testid={`pattern-panel-${index}`}>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h3 className="font-serif-display text-xl text-[#3F2D1B]">{panel.title}</h3>
        <span className="text-xs text-[#3F2D1B]/60" data-testid={`pattern-panel-n-${index}`}>n = {panel.n} · chance ≈ {panel.expected_pct}%</span>
      </div>
      {panel.multi && panel.n > 0 && (
        <p className="text-[11px] text-[#3F2D1B]/60 mt-1" data-testid={`pattern-panel-multi-note-${index}`}>
          % = share of charts with that planet in this house (a chart can count for several planets) · house empty in {panel.empty_house_count} of {panel.n} charts
        </p>
      )}
      {panel.n === 0 ? (
        <p className="text-sm text-[#3F2D1B]/60 mt-4">No usable charts for this question in this category.</p>
      ) : (
        <div className="grid lg:grid-cols-[1.6fr_1fr] gap-6 mt-4">
          <Distribution panel={panel} />
          <div>
            <Donut panel={panel} />
            <Ranking panel={panel} />
          </div>
        </div>
      )}
      {panel.top_samples?.length > 0 && (
        <p className="text-xs text-[#3F2D1B]/70 mt-4" data-testid={`pattern-samples-${index}`}>
          <span className="font-semibold">Examples with {panel.top.display}:</span> {panel.top_samples.join(" · ")}
        </p>
      )}
    </article>
  );
}

const FAKE = [6, 14, 9, 18, 4, 11, 7, 16, 5, 12, 8, 10];

export function LockedPanelPreview({ title }) {
  const data = FAKE.map((v, i) => ({ k: i, v }));
  return (
    <div className="rounded-2xl bg-white border border-amber-200/70 p-5">
      <h3 className="font-serif-display text-xl text-[#3F2D1B]">{title}</h3>
      <ResponsiveContainer width="100%" height={180}>
        <BarChart data={data}><Bar dataKey="v" radius={[6, 6, 0, 0]}>{data.map((d) => <Cell key={d.k} fill={PATTERN_COLORS[d.k]} />)}</Bar></BarChart>
      </ResponsiveContainer>
    </div>
  );
}
